// The consent notice a clinic's patients are shown, and how they accept it.
//
// One component, two mounts: the operator console (configuring a client) and the
// owner's own Settings. The notice is the CLIENT's legal document — they are the
// responsable under LFPDPPP and we are only the encargado — so the copy speaks
// to the business about its own notice, never about "the platform's".
//
// Everything here changes what real patients receive on their first message, so
// the destructive-ish parts say so plainly rather than relying on the operator
// to know: bumping the version re-asks everyone, and a blank link means we send
// ours instead of theirs.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import {
  type ConsentConfig,
  type ConsentConfigPatch,
  getMyConsentConfig,
  getTenantConsentConfig,
  saveMyConsentConfig,
  saveTenantConsentConfig,
} from './consent-data.js'
import {
  Banner,
  Button,
  Card,
  FormField,
  Input,
  Select,
  Spinner,
  Textarea,
} from '../../design-system/index.js'

export interface ConsentSettingsProps {
  /** Operator mount: the client being configured. Omit for the owner's own. */
  tenantId?: string
}

interface Draft {
  termsVersion: string
  privacyNoticeUrl: string
  acceptanceMode: 'implicit' | 'affirmative'
  consentMessage: string
  consentMessageUpdated: string
  consentDeclineMessage: string
  retryCooldownHours: string
  maxPromptCycles: string
  affirmativePhrases: string
}

function draftFrom(c: ConsentConfig): Draft {
  return {
    termsVersion: c.termsVersion,
    privacyNoticeUrl: c.privacyNoticeUrl,
    acceptanceMode: c.acceptanceMode,
    consentMessage: c.consentMessage,
    consentMessageUpdated: c.consentMessageUpdated,
    consentDeclineMessage: c.consentDeclineMessage,
    retryCooldownHours: String(c.retryCooldownHours),
    maxPromptCycles: String(c.maxPromptCycles),
    affirmativePhrases: c.affirmativePhrases.join(', '),
  }
}

export function ConsentSettings({ tenantId }: ConsentSettingsProps): JSX.Element {
  const qc = useQueryClient()
  const key = tenantId === undefined ? ['settings', 'consent'] : ['admin', 'consent', tenantId]

  const { data, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: () =>
      tenantId === undefined ? getMyConsentConfig() : getTenantConsentConfig(tenantId),
  })

  const [draft, setDraft] = useState<Draft | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  // Seed the form once the server value arrives, and re-seed after a save so the
  // fields show what was actually stored rather than what was typed.
  useEffect(() => {
    if (data) setDraft(draftFrom(data))
  }, [data])

  if (isLoading || !draft) {
    return (
      <Card>
        <Card.Head title="Aviso de privacidad" sub="Cargando…" />
        <Card.Body>
          <Spinner label="Cargando…" />
        </Card.Body>
      </Card>
    )
  }

  if (isError || !data) {
    return (
      <Card>
        <Card.Head title="Aviso de privacidad" />
        <Card.Body>
          <Banner tone="warn">
            No pudimos cargar esta configuración. Vuelve a intentar en un momento.
          </Banner>
        </Card.Body>
      </Card>
    )
  }

  const versionChanged = draft.termsVersion.trim() !== data.termsVersion
  const set = <K extends keyof Draft>(k: K, v: Draft[K]): void => {
    setDraft((d) => (d === null ? d : { ...d, [k]: v }))
    setSaved(false)
    setError('')
  }

  async function onSave(): Promise<void> {
    if (draft === null) return
    setSaving(true)
    setError('')
    const patch: ConsentConfigPatch = {
      termsVersion: draft.termsVersion.trim(),
      privacyNoticeUrl: draft.privacyNoticeUrl.trim(),
      acceptanceMode: draft.acceptanceMode,
      consentMessage: draft.consentMessage.trim(),
      consentMessageUpdated: draft.consentMessageUpdated.trim(),
      consentDeclineMessage: draft.consentDeclineMessage.trim(),
      retryCooldownHours: Number(draft.retryCooldownHours) || 24,
      maxPromptCycles: Number(draft.maxPromptCycles) || 3,
      affirmativePhrases: draft.affirmativePhrases
        .split(',')
        .map((p) => p.trim())
        .filter((p) => p !== ''),
    }
    try {
      const next =
        tenantId === undefined
          ? await saveMyConsentConfig(patch)
          : await saveTenantConsentConfig(tenantId, patch)
      qc.setQueryData(key, next)
      setDraft(draftFrom(next))
      setSaved(true)
    } catch (err) {
      // Surface the API's own message: it is the one that says WHICH field is
      // wrong (a missing {aviso}, a non-https link), and a generic "try again"
      // would leave someone guessing.
      const detail = (err as { response?: { data?: { error?: { message?: string } } } }).response
        ?.data?.error?.message
      setError(detail ?? 'No se pudo guardar. Vuelve a intentar.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <Card.Head
        title="Aviso de privacidad"
        sub="Lo que ve un paciente la primera vez que escribe, antes de que el agente responda."
      />
      <Card.Body>
        <div className="flex flex-col gap-5">
          {!data.usingOwnNotice && (
            <Banner tone="warn">
              Todavía no hay un aviso propio, así que enviamos el de INHERTA (
              {data.defaults.privacyNoticeUrl}). Tu clínica es la responsable de los datos, así que
              conviene publicar el tuyo y ponerlo aquí.
            </Banner>
          )}

          <FormField
            label="Enlace a tu aviso de privacidad"
            htmlFor="consent-url"
            helper="Debe empezar con https. Si lo dejas vacío enviamos el aviso de INHERTA."
          >
            <Input
              value={draft.privacyNoticeUrl}
              placeholder={data.defaults.privacyNoticeUrl}
              onChange={(e) => set('privacyNoticeUrl', e.target.value)}
            />
          </FormField>

          <FormField
            label="Cómo acepta el paciente"
            htmlFor="consent-mode"
            helper={
              draft.acceptanceMode === 'implicit'
                ? 'Enviamos el aviso y, si la persona sigue escribiendo, se toma como aceptado.'
                : 'La persona tiene que aceptar. Si no lo hace, preguntamos una vez más explicando que sin su aceptación no podemos atenderla, y si vuelve a negarse cerramos la conversación con un mensaje final. El agente ya no responde, y tu equipo tampoco recibe la conversación: sin aceptación no podemos tratar sus datos. Si más adelante escribe "acepto", continúa con normalidad.'
            }
          >
            <Select
              value={draft.acceptanceMode}
              onChange={(e) => set('acceptanceMode', e.target.value as Draft['acceptanceMode'])}
              options={[
                { value: 'implicit', label: 'Al continuar la conversación' },
                { value: 'affirmative', label: 'Respondiendo "acepto"' },
              ]}
            />
          </FormField>

          {draft.acceptanceMode === 'affirmative' && (
            <FormField
              label="Respuestas que cuentan como aceptación"
              htmlFor="consent-phrases"
              helper={`Separadas por comas. Si lo dejas vacío usamos: ${data.defaults.affirmativePhrases.join(', ')}`}
            >
              <Input
                value={draft.affirmativePhrases}
                placeholder={data.defaults.affirmativePhrases.join(', ')}
                onChange={(e) => set('affirmativePhrases', e.target.value)}
              />
            </FormField>
          )}

          {draft.acceptanceMode === 'affirmative' && (
            <FormField
              label="Horas de espera antes de volver a preguntar"
              htmlFor="consent-cooldown"
              helper="Si alguien no acepta, esperamos este tiempo antes de volver a enviarle el aviso cuando escriba de nuevo. Mínimo 1 hora: sin espera, cada mensaje generaría un aviso nuevo."
            >
              <Input
                type="number"
                min={1}
                max={720}
                value={draft.retryCooldownHours}
                onChange={(e) => set('retryCooldownHours', e.target.value)}
              />
            </FormField>
          )}

          {draft.acceptanceMode === 'affirmative' && (
            <FormField
              label="Cuántas veces volvemos a preguntar"
              htmlFor="consent-cycles"
              helper="Después de este número de intentos dejamos de enviarle avisos definitivamente. Sigue pudiendo aceptar si escribe que acepta; lo que se detiene son nuestros mensajes. Máximo 10, para que el aviso no se vuelva spam."
            >
              <Input
                type="number"
                min={1}
                max={10}
                value={draft.maxPromptCycles}
                onChange={(e) => set('maxPromptCycles', e.target.value)}
              />
            </FormField>
          )}

          {draft.acceptanceMode === 'affirmative' && (
            <FormField
              label="Mensaje si no acepta"
              htmlFor="consent-decline"
              helper="Se envía una sola vez, después de preguntar dos veces. Conviene poner un teléfono para que la persona pueda comunicarse de otra forma."
            >
              <Textarea
                autoGrow
                maxLength={900}
                value={draft.consentDeclineMessage}
                onChange={(e) => set('consentDeclineMessage', e.target.value)}
              />
            </FormField>
          )}

          <FormField
            label="Mensaje del aviso"
            htmlFor="consent-message"
            helper="Opcional. Si lo dejas vacío usamos el mensaje predeterminado. Escribe {aviso} donde va el enlace y {negocio} donde va el nombre de tu clínica."
          >
            <Textarea
              autoGrow
              maxLength={900}
              value={draft.consentMessage}
              onChange={(e) => set('consentMessage', e.target.value)}
            />
          </FormField>

          <FormField
            label="Mensaje cuando actualizas el aviso"
            htmlFor="consent-message-updated"
            helper="Opcional. Se envía a quien ya había aceptado una versión anterior."
          >
            <Textarea
              autoGrow
              maxLength={900}
              value={draft.consentMessageUpdated}
              onChange={(e) => set('consentMessageUpdated', e.target.value)}
            />
          </FormField>

          <FormField
            label="Versión del aviso"
            htmlFor="consent-version"
            helper="Súbela solo cuando cambie el contenido de tu aviso."
          >
            <Input
              value={draft.termsVersion}
              onChange={(e) => set('termsVersion', e.target.value)}
            />
          </FormField>

          {versionChanged && (
            <Banner tone="warn">
              Al cambiar la versión, todos tus pacientes verán el aviso otra vez la próxima vez que
              escriban, y tendrán que aceptarlo de nuevo.
            </Banner>
          )}

          {error !== '' && <Banner tone="sens">{error}</Banner>}
          {saved && <Banner tone="ok">Guardado. Aplica a partir del siguiente mensaje.</Banner>}

          <div className="flex justify-end">
            <Button onClick={() => void onSave()} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
